/**
 * 权益详情辅助工具
 * 集成了 RightsManager 和 detailPopupService
 */
import { CreateElement, VNode } from 'vue';
import { showDetailPopup, hideDetailPopup } from './detailPopupService';
import { rightsManager } from '../RightsManager';

interface PopupResult {
  success: boolean;
  action: string;
}

/**
 * 显示权益详情弹窗
 * 自动从 RightsManager 获取权益详情URL
 * @param customUrl 可选的自定义URL，如果不传则使用 RightsManager 中的配置
 * @param options 其他弹窗配置项
 * @returns Promise<PopupResult> 返回弹窗操作结果
 */
export const showRightsDetail = async (
  customUrl?: string,
  options?: {
    popupTitle?: string;
    fullScreen?: boolean;
    showUnopenedDetailUrl?: boolean;
    showClose?: boolean;
    headerSlot?: (h: CreateElement) => VNode;
    onChangeClose?: (closeStatus: boolean) => void;
  },
): Promise<PopupResult> => {
  try {
    // 获取埋点管理器
    const trackingManager = rightsManager.getTrackingManager();
    const contextValue = rightsManager.context.value;

    // 上报查看详情点击埋点
    trackingManager.trackDetailClick(contextValue);

    // 更新上下文状态 - 标记详情弹窗正在显示
    await rightsManager.updateContext({
      detailShow: true,
    }, false);

    // 显示详情弹窗
    const result = await showDetailPopup({
      rightsUrl: customUrl,
      popupTitle: options?.popupTitle,
      fullScreen: options?.fullScreen || false,
      showUnopenedDetailUrl: options?.showUnopenedDetailUrl || false,
      showClose: options?.showClose || true,
      headerSlot: options?.headerSlot,
      onChangeClose: options?.onChangeClose,
      onClose: () => {
        // 上报详情关闭埋点
        const closeContextValue = rightsManager.context.value;
        trackingManager.trackDetailClose(closeContextValue);

        // 更新上下文状态 - 标记详情弹窗已关闭
        rightsManager.updateContext({
          detailShow: false,
        }, false);
      },
    });

    return result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[showRightsDetail] 显示详情弹窗失败:', error);
    // 确保状态被重置
    await rightsManager.updateContext({
      detailShow: false,
    }, false);
    throw error;
  }
};

/**
 * 隐藏权益详情弹窗
 */
export const hideRightsDetail = (): void => {
  // 获取埋点管理器并上报关闭埋点
  const trackingManager = rightsManager.getTrackingManager();
  const contextValue = rightsManager.context.value;
  trackingManager.trackDetailClose(contextValue);

  hideDetailPopup();
  // 更新上下文状态
  rightsManager.updateContext({
    detailShow: false,
  }, false);
};
